import { fetchTableData, insertRow, updateRow, deleteRow, isApiAvailable } from './dbViewerService';
import { getTableData, saveTableData, getNextId } from './tables';
import { enqueue as outboxEnqueue, isNetworkError } from '../../../lib/outbox';
import { handleSessionExpired } from '../../../lib/authGuard';

/** A 401 means the session is dead — don't silently fall back to stale local data. */
function isAuthError(err: any): boolean {
  return !!err && err.status === 401;
}

// Apply a write to the local cache so the UI stays consistent while it's queued offline.
function applyLocalUpdate(tableName: string, id: number | string, updates: Record<string, any>, pkColumn?: string) {
  const data = getTableData(tableName);
  const pk = pkColumn || guessIdColumn(tableName);
  // eslint-disable-next-line eqeqeq
  const idx = data.findIndex(r => r[pk] == id);
  if (idx >= 0) {
    data[idx] = { ...data[idx], ...updates };
    saveTableData(tableName, data);
  }
}

let _apiChecked = false;
let _apiAvailable = false;
let _checkPromise: Promise<boolean> | null = null;

export async function checkApi(): Promise<boolean> {
  // Cache only the POSITIVE result. If the API was unavailable (e.g. the backend was
  // momentarily down/restarting), keep re-probing so a recovered backend is picked up
  // on the next data load — instead of being permanently stuck in localStorage mode
  // until a full page reload.
  if (_apiChecked && _apiAvailable) return true;
  if (_checkPromise) return _checkPromise;
  _checkPromise = isApiAvailable().then(v => {
    _apiAvailable = v;
    _apiChecked = true;
    _checkPromise = null;
    return v;
  }).catch(() => {
    _apiAvailable = false;
    _apiChecked = true;
    _checkPromise = null;
    return false;
  });
  return _checkPromise;
}

export function isApiReady(): boolean {
  return _apiChecked && _apiAvailable;
}

export async function loadTable(tableName: string): Promise<Record<string, any>[]> {
  const useApi = await checkApi();
  if (useApi) {
    try {
      const rows = await fetchTableData(tableName);
      // Sync the fresh API data into the local cache so getTableData() (used all
      // over the app after a loadTable) returns current data, not a stale copy.
      try { saveTableData(tableName, rows); } catch { /* best-effort (e.g. quota) */ }
      return rows;
    } catch (err) {
      // Session expired/invalid → bounce to login instead of showing stale local data.
      if (isAuthError(err)) { handleSessionExpired(); return []; }
      return getTableData(tableName);
    }
  }
  return getTableData(tableName);
}

export async function saveRow(
  tableName: string,
  id: number | string,
  updates: Record<string, any>,
  pkColumn?: string
): Promise<Record<string, any>> {
  if (isApiReady()) {
    try {
      return await updateRow(tableName, id, updates);
    } catch (err) {
      if (isAuthError(err)) { handleSessionExpired(); throw err; }
      if (!isNetworkError(err)) throw err;       // real server error — surface it
      // Offline: queue for sync + apply locally so the UI keeps working.
      outboxEnqueue({ table: tableName, type: 'update', rowId: id, payload: updates, label: `Update ${tableName} #${id}` });
      applyLocalUpdate(tableName, id, updates, pkColumn);
      return { [pkColumn || guessIdColumn(tableName)]: id, ...updates };
    }
  }
  const data = getTableData(tableName);
  const pk = pkColumn || guessIdColumn(tableName);
  // eslint-disable-next-line eqeqeq
  const idx = data.findIndex(r => r[pk] == id);
  if (idx >= 0) {
    data[idx] = { ...data[idx], ...updates };
    saveTableData(tableName, data);
    return data[idx];
  }
  throw new Error(`Row ${id} not found in ${tableName}`);
}

export async function addRow(
  tableName: string,
  row: Record<string, any>,
  pkColumn?: string
): Promise<Record<string, any>> {
  if (isApiReady()) {
    try {
      return await insertRow(tableName, row);
    } catch (err) {
      if (isAuthError(err)) { handleSessionExpired(); throw err; }
      if (!isNetworkError(err)) throw err;       // real server error — surface it
      // Offline: optimistic local insert (temp id) + queue the real insert for sync.
      const data = getTableData(tableName);
      const pk = pkColumn || guessIdColumn(tableName);
      const tempId = getNextId(tableName, data);
      const optimistic = { [pk]: tempId, ...row, __pendingSync: 1 };
      data.push(optimistic);
      saveTableData(tableName, data);
      outboxEnqueue({ table: tableName, type: 'insert', payload: row, label: `Add to ${tableName}` });
      return optimistic;
    }
  }
  const data = getTableData(tableName);
  const pk = pkColumn || guessIdColumn(tableName);
  const newId = getNextId(tableName, data);
  const newRow = { [pk]: newId, ...row };
  data.push(newRow);
  saveTableData(tableName, data);
  return newRow;
}

export async function removeRow(
  tableName: string,
  id: number | string,
  pkColumn?: string
): Promise<void> {
  if (isApiReady()) {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const softDelete = { is_deleted: 1, updated_at: now };
    try {
      await updateRow(tableName, id, softDelete);
      return;
    } catch (err) {
      if (isAuthError(err)) { handleSessionExpired(); throw err; }
      if (!isNetworkError(err)) throw err;       // real server error — surface it
      outboxEnqueue({ table: tableName, type: 'update', rowId: id, payload: softDelete, label: `Delete ${tableName} #${id}` });
      applyLocalUpdate(tableName, id, softDelete, pkColumn);
      return;
    }
  }
  const data = getTableData(tableName);
  const pk = pkColumn || guessIdColumn(tableName);
  // eslint-disable-next-line eqeqeq
  const idx = data.findIndex(r => r[pk] == id);
  if (idx >= 0) {
    data[idx].is_deleted = 1;
    data[idx].updated_at = new Date().toISOString().replace('T', ' ').substring(0, 19);
    saveTableData(tableName, data);
  }
}

function guessIdColumn(tableName: string): string {
  const map: Record<string, string> = {
    users: 'user_id',
    user_businesses: 'business_id',
    user_family: 'family_id',
    user_client_relationships: 'relationship_id',
    user_account_access: 'access_id',
    dms_documents: 'document_id',
    dms_folders: 'folder_id',
    dms_folder_owners: 'folder_owner_id',
    acctg_coa: 'account_id',
  };
  return map[tableName] || tableName.replace(/s$/, '') + '_id';
}

export { getTableData, saveTableData, getNextId } from './tables';
export { insertRow, updateRow, deleteRow, fetchTableData } from './dbViewerService';
